import { useEffect, useMemo, useRef } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectSite } from '../../../store/authReducer';
import { selectMultiSiteSelection, selectSitesBasic } from '../../site-selection/reducer';
import { getSiteLabel } from '../../site-selection/helpers';
import { fetchInventoryTypes } from '../../inventory/actions';
import {
  fetchAssetTemplates,
  fetchAssetGroups,
  fetchAssetItems,
  fetchAssetTaskOccurrences,
  fetchAssetSiteInventories,
  fetchAssetTemplateOccurrences,
  fetchAssetGroupDetail,
  fetchAssetItemDetail,
  resetAssetView,
} from '../actions';
import {
  selectAssetTemplates,
  selectAssetGroups,
  selectAssetItems,
  selectAssetTaskOccurrences,
  selectAssetSiteInventories,
  selectAssetTemplateOccurrences,
  selectSelectedOccurrenceId,
  selectAssetTree,
  selectSelectedTemplateId,
  selectSelectedNodeId,
  selectExpandedNodeIds,
  selectAssetSearchTerm,
  selectAssetLoading,
  selectAssetError,
  selectAddTemplateDrawerOpen,
  selectNodeActionDrawer,
  selectSelectedSidebarItem,
} from '../reducer';

/**
 * Everything the assets page reads from the store, plus the fetches that keep
 * it current. Split out of AssetsContainer so the container composes state
 * rather than assembling it.
 */
export function useAssetFetching() {
  const dispatch = useDispatch();

  const site = useSelector(selectSite);
  const sites = useSelector(selectSitesBasic);
  const multiSiteSelection = useSelector(selectMultiSiteSelection, shallowEqual);
  // The cart header names the site the way the site picker does -- its short
  // code (MI03), not the internal id.
  const siteLabel = useMemo(() => getSiteLabel(sites || [], site), [sites, site]);

  const templates = useSelector(selectAssetTemplates);
  const groups = useSelector(selectAssetGroups);
  const items = useSelector(selectAssetItems);
  const siteInventories = useSelector(selectAssetSiteInventories);
  const templateOccurrences = useSelector(selectAssetTemplateOccurrences);
  const selectedOccurrenceId = useSelector(selectSelectedOccurrenceId);
  const tree = useSelector(selectAssetTree);
  const assetTaskOccurrences = useSelector(selectAssetTaskOccurrences);
  const selectedTemplateId = useSelector(selectSelectedTemplateId);
  const selectedNodeId = useSelector(selectSelectedNodeId);
  const expandedNodeIds = useSelector(selectExpandedNodeIds);
  const searchTerm = useSelector(selectAssetSearchTerm);
  const loading = useSelector(selectAssetLoading);
  const error = useSelector(selectAssetError);
  const addTemplateDrawerOpen = useSelector(selectAddTemplateDrawerOpen);
  const nodeActionDrawer = useSelector(selectNodeActionDrawer);
  const selectedSidebarItem = useSelector(selectSelectedSidebarItem);

  const siteRef = useRef(site);
  const previousSiteKeyRef = useRef<string | null>(null);

  useEffect(() => {
    siteRef.current = site;
  }, [site]);

  useEffect(() => {
    if (!site) return;
    // Templates, groups and items are all site-scoped. Drop the previous site's
    // rows and selection before refetching, or the old template stays open and
    // the auto-select picks from a list that belongs to the other site.
    const siteKey = `${site}|${JSON.stringify(multiSiteSelection ?? null)}`;
    if (previousSiteKeyRef.current !== null && previousSiteKeyRef.current !== siteKey) {
      dispatch(resetAssetView());
    }
    previousSiteKeyRef.current = siteKey;

    dispatch(fetchAssetTemplates());
    dispatch(fetchAssetGroups());
    dispatch(fetchAssetItems());
    dispatch(fetchAssetTaskOccurrences());
    dispatch(fetchAssetSiteInventories());
    dispatch(fetchAssetTemplateOccurrences());
    // Populates the type dropdown in the shared Add Inventory modal, which this
    // page can open without the Inventory page ever having been visited.
    dispatch(fetchInventoryTypes());
  }, [dispatch, site, multiSiteSelection]);

  const selectedSidebarItemType = selectedSidebarItem?.itemType;
  const selectedSidebarItemId = selectedSidebarItem?.id;
  useEffect(() => {
    if (selectedSidebarItemId == null) return;
    if (selectedSidebarItemType === 'group') {
      dispatch(fetchAssetGroupDetail(selectedSidebarItemId));
    } else if (selectedSidebarItemType === 'item') {
      dispatch(fetchAssetItemDetail(selectedSidebarItemId));
    }
  }, [selectedSidebarItemType, selectedSidebarItemId, groups, items, dispatch]);

  const activeTemplates = useMemo(
    () => (templates || []).filter((t: any) => !t.archivedAt),
    [templates]
  );
  const activeGroups = useMemo(() => (groups || []).filter((g: any) => !g.archivedAt), [groups]);
  const activeItems = useMemo(() => (items || []).filter((i: any) => !i.archivedAt), [items]);

  // Only unattached rows can be linked to an asset from the drawer.
  const linkableTaskOccurrences = useMemo(
    () =>
      (assetTaskOccurrences || []).filter(
        (o: any) =>
          !o?.archivedAt &&
          o?.assetGroupId == null &&
          o?.assetItemId == null &&
          o?.assetTemplateId == null &&
          o?.completedAt == null
      ),
    [assetTaskOccurrences]
  );

  const linkableInventoryItems = useMemo(
    () =>
      (siteInventories || []).filter(
        (inv: any) =>
          inv && !inv.archivedAt && inv.assetItemId == null && inv.assetTemplateId == null
      ),
    [siteInventories]
  );

  return {
    site,
    siteRef,
    siteLabel,
    templates,
    groups,
    items,
    siteInventories,
    templateOccurrences,
    selectedOccurrenceId,
    tree,
    selectedTemplateId,
    selectedNodeId,
    expandedNodeIds,
    searchTerm,
    loading,
    error,
    addTemplateDrawerOpen,
    nodeActionDrawer,
    selectedSidebarItem,
    activeTemplates,
    activeGroups,
    activeItems,
    linkableTaskOccurrences,
    linkableInventoryItems,
  };
}
